import { Box, Grid2, TextField } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../hooks/useNavigationGuard";
import {
  checkCanUpdate,
  notifyError,
  notifySuccess,
} from "../../../utilities/helper";
import CustomSwitch from "../../../components/CustomSwitch/CustomSwitch";
import { MenuContext } from "../../../App";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";
import {
  createCohortService,
  fetchCohortByIdService,
  updateCohortService,
} from "../../../services/cohortManagementService";

const AddUpdateCohortManagement = () => {
  const id = useLocation()?.state?.id;

  const [load, setLoad] = useState(false);
  const [canUpdateCohort, setCanUpdateCohort] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const navigate = useNavigate();

  const access_token = localStorage?.getItem("access_token");

  const { systemActions } = useContext(MenuContext);
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();

  const role = localStorage.getItem("role");

  const cohortCreateSchema = yup.object().shape({
    name: yup
      .string()
      .required("Name is required")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "Value must not start with a number or special character"
      )
      .test("has-letter", "Value must contain at least one alphabet", (value) =>
        /[A-Za-z]/.test(value || "")
      ),

    capacity: yup
      .number()
      .typeError("Capacity must be a number")
      .required("Capacity is required")
      .min(0, "Capacity cannot be less than 0")
      .max(999999999, "Capacity cannot be greater than 999999999")
      .integer("Capacity must be an integer"),

    notes: yup
      .string()
      .required("Notes is required")
      .test(
        "not-only-spaces",
        "Notes cannot be only spaces",
        (value) => value && value.trim().length > 0
      ),
    isActive: yup.boolean(),
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(cohortCreateSchema),
    defaultValues: {
      name: "",
      capacity: "",
      notes: "",
      isActive: false,
    },
  });

  useNavigationGuard(isDirty);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin" && id) {
      let canUpdate = false;
      const updateFound = systemActions?.find(
        (item) => item?.name === "CanAddEditCohorts"
      );
      if (updateFound) {
        canUpdate = true;
      }
      setCanUpdateCohort(canUpdate);
    } else {
      if (!id || role === "Admin") {
        setCanUpdateCohort(true);
      }
    }
  }, [systemActions, id]);

  useEffect(() => {
    if (id) {
      getCohortById(id);
    }
  }, [id]);

  useEffect(() => {
    if (canUpdateCohort) {
      setIsReadOnly(false);
    } else {
      setIsReadOnly(true);
    }
  }, [canUpdateCohort]);

  const getCohortById = async (id) => {
    setLoad(true);
    try {
      const res = await fetchCohortByIdService(access_token, id);
      if (res?.status == 200) {
        const resp = res?.data?.result;
        setLoad(false);
        reset({
          capacity: resp?.capacity,
          name: resp?.name,
          notes: resp?.notes,
          isActive: resp?.isActive,
        });
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err?.message);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const addUpdateCohort = async (data) => {
    setLoad(true);
    const newData = {
      name: data?.name,
      capacity: Number(data?.capacity),
      notes: data?.notes,
      isActive: data?.isActive,
    };
    if (id) {
      newData.id = id;
    }
    try {
      if (!id) {
        const res = await createCohortService(access_token, newData);
        if (res?.data?.status == 409) {
          setLoad(false);
          notifyError("Cohort already exists !");
          return;
        } else if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Cohort is created successfully !");
          reset();
          navigate("/cohort-management");
        }
      } else {
        const res = await updateCohortService(access_token, newData);
        if (res?.data?.status == 400) {
          setLoad(false);
          notifyError("Cohort name already exists !");
        } else if (res?.data?.status == 201) {
          setLoad(false);
          notifySuccess("Cohort is Updated successfully !");
          reset();
          navigate("/cohort-management");
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const { onChange, ...rest } = register("capacity");
  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(
                  e,
                  isDirty,
                  "cohort-management",
                  navigate
                )
              }
            >
              Cohort Management
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              to="/addUpdate-cohort-management"
            >
              {id ? "Edit Cohort" : "Add Cohort"}
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Cohort Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(addUpdateCohort)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Name
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    fullWidth
                    type={"text"}
                    disabled={isReadOnly}
                    {...register("name")}
                  />
                </div>
                {errors.name && (
                  <div className="error-container">{errors.name.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Capacity
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    fullWidth
                    disabled={isReadOnly}
                    type={"text"}
                    {...rest}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
                      if (value !== "0") value = value.replace(/^0+/, ""); // Remove leading zeros
                      e.target.value = value;
                      onChange(e);
                    }}
                  />
                </div>
                {errors.capacity && (
                  <div className="error-container">
                    {errors.capacity.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Notes
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    fullWidth
                    type={"text"}
                    disabled={isReadOnly}
                    {...register("notes")}
                  />
                </div>
                {errors.notes && (
                  <div className="error-container">{errors.notes.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Is Active</p>
                  <Controller
                    name="isActive"
                    control={control}
                    disabled={isReadOnly}
                    defaultValue={false} // or your actual initial value
                    render={({ field }) => (
                      <CustomSwitch
                        isSwitch={field.value}
                        disabled={isReadOnly}
                        setSwitch={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </div>
              </Grid2>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "cohort-management",
                      navigate
                    );
                  }}
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                {checkCanUpdate(id, isReadOnly, role) && (
                  <button
                    type="submit"
                    disabled={isReadOnly}
                    className={isReadOnly ? "disabled-btn" : "create-btn"}
                  >
                    {id ? "Update" : "Save"}
                  </button>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default AddUpdateCohortManagement;
